import DateInput from "@/components/shared/forms/DateInput";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import RadioSelect from "@/components/shared/forms/RadioSelect";
import ReadOnlyDateInput from "@/components/shared/forms/ReadOnlyDateInput";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import { CHLD } from "@/models/chld.tsx";
import { useCoreData } from "@/queries.ts";
import {
  MultiSelect,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { useRouteContext } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { UseFormReturn, useWatch } from "react-hook-form";
import { FaBriefcase } from "react-icons/fa";
import { FiUser } from "react-icons/fi";
import { SSIDWarning } from "@/components/records/child/SSIDWarning";
import { useMultiSelectChipListLabel } from "@/components/shared/forms/useMultiSelectChipListLabel";
import { useMultiSelectListboxMultiselectable } from "@/components/shared/forms/useMultiSelectListboxMultiselectable";
import { multiSelectSelectedItemRender } from "@/components/shared/forms/multiSelectSelectedItemRender";
import { MultiSelectToggleButton } from "@/components/shared/forms/MultiSelectToggleButton";
import { MultiSelectClearButton } from "@/components/shared/forms/MultiSelectClearButton";
import { multiSelectTagRender } from "@/components/shared/forms/multiSelectTagRender";

type RaceOption = {
  Text: string;
  Value: string;
};

type RaceMultiSelectProps = {
  id?: string;
  ariaLabel?: string;
  disabled: boolean;
  options: RaceOption[];
  initialValues: string[];
  onChangeValues: (values: string[]) => void;
};

function RaceMultiSelect({
  id,
  ariaLabel,
  disabled,
  options,
  initialValues,
  onChangeValues,
}: RaceMultiSelectProps) {
  const [selected, setSelected] = useState<RaceOption[]>(() => {
    const initialSet = initialValues.filter(Boolean);
    return initialSet
      .map((val) => options.find((o) => o.Value === val))
      .filter((o): o is RaceOption => !!o);
  });

  const handleChange = (e: MultiSelectChangeEvent) => {
    const next = (e.value as RaceOption[]) ?? [];
    const limited = next.slice(0, 5);

    setSelected(limited);
    onChangeValues(limited.map((o) => o.Value));
  };

  // See MultiSelectToggleButton: gives keyboard users a way to browse the
  // option list without having to type first.
  const [opened, setOpened] = useState(false);

  useMultiSelectChipListLabel(id, ariaLabel, selected);
  useMultiSelectListboxMultiselectable(id, opened);

  return (
    <MultiSelect
      id={id}
      ariaLabel={ariaLabel}
      data={options}
      textField="Text"
      dataItemKey="Value"
      value={selected}
      onChange={handleChange}
      disabled={disabled}
      style={{ width: "100%" }}
      placeholder="Select up to five races"
      opened={opened}
      onOpen={() => setOpened(true)}
      onClose={() => setOpened(false)}
      itemRender={multiSelectSelectedItemRender}
      popupSettings={id ? { className: `${id}-listbox-popup` } : undefined}
      tagRender={multiSelectTagRender}
      suffix={
        <>
          <MultiSelectToggleButton
            opened={opened}
            onToggle={() => setOpened((prev) => !prev)}
            disabled={disabled}
          />
          {selected.length > 0 && (
            <MultiSelectClearButton
              onClear={() =>
                handleChange({ value: [] } as MultiSelectChangeEvent)
              }
              disabled={disabled}
            />
          )}
        </>
      }
    />
  );
}

type LockedFields = {
  csppid: boolean;
  cicn: boolean;
  firstName: boolean;
  lastName: boolean;
  dob: boolean;
  preschool: boolean;
};

function validateProgramYearDate(
  effectiveStartDate: string | undefined,
  programYear: string | undefined
): true | string {
  if (!effectiveStartDate || !programYear) {
    return true;
  }

  const programYearMatch = programYear.match(/^(\d{4})-(\d{4})$/);
  if (!programYearMatch) {
    return true;
  }

  const startYear = parseInt(programYearMatch[1], 10);
  const endYear = parseInt(programYearMatch[2], 10);

  const programYearStart = new Date(startYear, 6, 1, 0, 0, 0, 0);
  const programYearEnd = new Date(endYear, 5, 30, 23, 59, 59, 999);

  let effectiveDate: Date;

  if (effectiveStartDate.includes("/")) {
    const [month, day, year] = effectiveStartDate.split("/").map(Number);
    effectiveDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  } else if (effectiveStartDate.includes("-")) {
    const [year, month, day] = effectiveStartDate.split("-").map(Number);
    effectiveDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  } else {
    effectiveDate = new Date(effectiveStartDate);
    effectiveDate.setHours(0, 0, 0, 0);
  }

  if (isNaN(effectiveDate.getTime())) {
    return true;
  }

  if (effectiveDate < programYearStart || effectiveDate > programYearEnd) {
    return `Effective Start Date must be between 07/01/${startYear} and 06/30/${endYear} for program year ${programYear}`;
  }

  return true;
}

export function CHLDWizardForm({
  form,
  isNewRecord,
  lockedFields,
}: {
  form: UseFormReturn<CHLD, any, CHLD>;
  isNewRecord: boolean;
  lockedFields: LockedFields;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const viewOnly = !leaContext.hasRole("CHLDEdit");
  const coreData = useCoreData();
  const tooltipMap = leaContext.coreData.tooltipMap.get("CHLD");

  if (!coreData) {
    return <div>Loading...</div>;
  }

  const {
    control,
    register,
    setValue,
    clearErrors,
    watch,
    trigger,
    formState: { errors },
  } = form;

  const formValues = watch();

  const childCountryOfBirth = useWatch({
    control,
    name: "ChildCountryOfBirth",
  });

  const programYear = useWatch({
    control,
    name: "ProgramYear",
  });

  const effectiveStartDate = useWatch({
    control,
    name: "EffectiveStartDate",
  });

  const childEthnicity = useWatch({
    control,
    name: "ChildEthnicity",
  });

  const childRace1 = useWatch({
    control,
    name: "ChildRace1",
  });

  const ssidValue = useWatch({
    control,
    name: "SSID",
  });

  const iepIfspValue = useWatch({
    control,
    name: "IEPIFSPIndicator",
  });

  const preschoolOptions = leaContext
    .currentOrgPreschoolsByProgramYear(programYear)
    .map((x) => ({
      Text: x.PreschoolName,
      Value: x.PreschoolCDSCode,
    }));

  const stateOptions = (() => {
    const filtered = coreData.states.filter(
      (x) => x.CountryCode === childCountryOfBirth
    );

    if (childCountryOfBirth !== "US") return filtered;

    const i = filtered.findIndex((x) => x.Name === "California");
    if (i <= 0) return filtered;

    return [filtered[i], ...filtered.slice(0, i), ...filtered.slice(i + 1)];
  })();

  const isDomesticCountry =
    childCountryOfBirth === "US" ||
    childCountryOfBirth === "CA" ||
    childCountryOfBirth === "MX";

  useEffect(() => {
    clearErrors(["ChildStateOfBirth", "ChildCityOfBirth"]);

    if (!isDomesticCountry) {
      setValue("ChildStateOfBirth", "", {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [childCountryOfBirth, isDomesticCountry, clearErrors, setValue]);

  useEffect(() => {
    if (effectiveStartDate && programYear) {
      trigger("EffectiveStartDate");
    }
  }, [programYear, effectiveStartDate, trigger]);

  useEffect(() => {
    if (childEthnicity === "Y" || childEthnicity === "N") {
      setValue("ChildEthnicityMissingIndicator", "N", { shouldDirty: true });
    } else if (childEthnicity === "") {
      setValue("ChildEthnicityMissingIndicator", "Y", { shouldDirty: true });
    }
  }, [childEthnicity, setValue]);

  useEffect(() => {
    if (childRace1 === "" || childRace1 === null || childRace1 === undefined) {
      setValue("ChildRaceMissingIndicator", "Y", { shouldDirty: true });
    } else {
      setValue("ChildRaceMissingIndicator", "N", { shouldDirty: true });
    }
  }, [childRace1, setValue]);

  return (
    <Container
      className="form-style overview-container ps-3 pe-3"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaBriefcase className="section-icon" />
          <h3 className="section-title">Agency Information</h3>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="CSPPID"
              labelText="CSPPID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!isNewRecord}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={lockedFields.csppid}
                  maxLength={50}
                  {...register(name, {
                    required: isNewRecord ? false : "CSPPID is required",
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="EffectiveStartDate"
              labelText="Effective Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(name, {
                    required: "Effective start date is required",
                    validate: (value) =>
                      validateProgramYearDate(value, programYear),
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, { required: "Program Year is required" })}
                  options={coreData.programYearList}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                lockedFields.preschool && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Preschool is required" }}
                    options={preschoolOptions}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, { required: "Preschool is required" })}
                    options={preschoolOptions}
                  />
                )
              }
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="LocalChildId"
              labelText="Local Child ID (CICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={lockedFields.cicn}
                  maxLength={50}
                  {...register(name, {
                    required: "Local Child ID (CICN) is required",
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="SSID"
              labelText="SSID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={10}
                  {...register(name, {
                    validate: (value: string | undefined) =>
                      !value || value.length === 10
                        ? true
                        : "SSID must be exactly 10 characters",
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FiUser className="section-icon" />
          <h3 className="section-title">Child Information</h3>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="ChildFirstName"
              labelText="First Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={lockedFields.firstName}
                  maxLength={30}
                  {...register(name, { required: "First name is required" })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildMiddleName"
              labelText="Middle Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={50}
                  {...register(name)}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildLastName"
              labelText="Last Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={lockedFields.lastName}
                  maxLength={30}
                  {...register(name, { required: "Last name is required" })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildNameSuffix"
              labelText="Suffix"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name)}
                  options={coreData.nameSuffixList}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="ChildCountryOfBirth"
              labelText="Country of Birth"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, {
                    required: "Country of birth is required",
                  })}
                  options={coreData.countryList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildStateOfBirth"
              labelText="State of Birth"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={isDomesticCountry}
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, {
                    validate: (value: string | undefined) => {
                      if (isDomesticCountry)
                        return value ? true : "State of birth is required";
                      return value
                        ? "State of birth must be empty for the selected country"
                        : true;
                    },
                  })}
                  options={stateOptions.map((x) => ({
                    Text: x.Name,
                    Value: x.Code,
                  }))}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildCityOfBirth"
              labelText="City of Birth"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={isDomesticCountry}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={30}
                  {...register(name, {
                    validate: (value: string | undefined) => {
                      if (isDomesticCountry)
                        return value ? true : "City of birth is required";
                      return true;
                    },
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildDateOfBirth"
              labelText="Date of Birth"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                lockedFields.dob && !viewOnly ? (
                  <ReadOnlyDateInput
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Date of birth is required" }}
                  />
                ) : (
                  <DateInput
                    id={id}
                    disabled={viewOnly}
                    maxLength={50}
                    {...register(name, {
                      required: "Date of birth is required",
                    })}
                  />
                )
              }
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildGender"
              labelText="Gender"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, { required: "Gender is required" })}
                  options={coreData.genderList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildEthnicity"
              labelText="Hispanic"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
              labelAs="legend"
            >
              {(id, name, validationProps) => (
                <div>
                  <RadioSelect
                    id={id}
                    disabled={viewOnly}
                    {...validationProps}
                    {...register(name, {
                      required: "Hispanic field is required",
                    })}
                    options={[
                      { Text: "Yes", Value: "Y" },
                      { Text: "No", Value: "N" },
                      { Text: "No Response", Value: "" },
                    ]}
                  />
                  <input
                    type="hidden"
                    {...register("ChildEthnicityMissingIndicator")}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ChildRace1"
              labelText="Race(s)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <div>
                  <input type="hidden" {...register("ChildRace1")} />
                  <input type="hidden" {...register("ChildRace2")} />
                  <input type="hidden" {...register("ChildRace3")} />
                  <input type="hidden" {...register("ChildRace4")} />
                  <input type="hidden" {...register("ChildRace5")} />
                  <input
                    type="hidden"
                    {...register("ChildRaceMissingIndicator")}
                  />

                  <RaceMultiSelect
                    id={id}
                    ariaLabel="Race(s)"
                    disabled={viewOnly}
                    options={coreData.raceList}
                    initialValues={[
                      formValues.ChildRace1 ?? "",
                      formValues.ChildRace2 ?? "",
                      formValues.ChildRace3 ?? "",
                      formValues.ChildRace4 ?? "",
                      formValues.ChildRace5 ?? "",
                    ]}
                    onChangeValues={(values) => {
                      const [r1, r2, r3, r4, r5] = values;

                      setValue("ChildRace1", r1 ?? "", { shouldDirty: true });
                      setValue("ChildRace2", r2 ?? "", { shouldDirty: true });
                      setValue("ChildRace3", r3 ?? "", { shouldDirty: true });
                      setValue("ChildRace4", r4 ?? "", { shouldDirty: true });
                      setValue("ChildRace5", r5 ?? "", { shouldDirty: true });
                    }}
                  />
                </div>
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="IEPIFSPIndicator"
              labelText="Active IEP or IFSP?"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, {
                    required: "Active IEP or IFSP is required",
                  })}
                  options={coreData.IEPorIFSPList}
                />
              )}
            </FormFieldRow>
            <SSIDWarning iepValue={iepIfspValue} ssidValue={ssidValue} />
          </div>
        </div>
      </div>

      <span
        id="spnAddOrEditClassroomErrorOverall"
        className="row ps-3 mt-3 pe-3 float-end"
        style={{ color: "#CC0000" }}
      ></span>
    </Container>
  );
}
